import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase-config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData.isAdmin) {
          toast.success('Welcome back, Admin!');
          navigate('/admin');
        } else {
          toast.success('Welcome back!');
          navigate('/client');
        }
      } else {
        toast.error('User data not found. Please contact support.');
        setError('User data not found. Please contact support.');
      }
    } catch (loginError) {
      console.error('Login error:', loginError.message);
      toast.error('Invalid email or password. Please try again.');
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '100vh', background: 'var(--surface-200)' }}>

      {/* Left panel — dark editorial */}
      <div style={{
        background: 'var(--text-primary)',
        padding: '64px 56px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '24px'
      }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(242,241,237,0.45)', margin: 0 }}>Client portal</p>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 700, letterSpacing: '-2px', color: '#f2f1ed', margin: 0, lineHeight: 1.05 }}>Welcome back.</h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '1rem', color: 'rgba(242,241,237,0.65)', lineHeight: 1.7, margin: 0 }}>Log in to access your dashboard and continue building your next-level website.</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {['Fast project management', 'Easy content editing', 'Real-time updates'].map(item => (
            <li key={item} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'rgba(242,241,237,0.6)', paddingLeft: '16px', position: 'relative' }}>
              <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '5px', height: '5px', borderRadius: '50%', background: 'var(--accent)', display: 'block' }} />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Right panel — form */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 56px', background: 'var(--surface-100)' }}>
        <form onSubmit={handleLogin} style={{ width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.75rem,3vw,2.25rem)', fontWeight: 700, letterSpacing: '-1.5px', color: 'var(--text-primary)', margin: '0 0 6px', lineHeight: 1.1 }}>Log in</h1>
            <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-secondary)', margin: 0 }}>Enter your credentials to access your account.</p>
          </div>

          {error && <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--accent)', margin: 0, padding: '10px 12px', background: 'rgba(245,78,0,0.06)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(245,78,0,0.15)' }}>{error}</p>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="email" style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-primary)', background: 'var(--surface-200)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="password" style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-primary)', background: 'var(--surface-200)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '12px 24px', fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1, transition: 'background 0.15s', marginTop: '4px' }}
          >
            {isLoading ? 'Logging in...' : 'Log in'}
          </button>
        </form>
      </div>

    </div>
  );
};

export default LoginPage;
